import {v4} from "uuid";
import {
    CreateOfferRequest,
    Currency,
    OfferSource,
    OfferStatus,
    OfferType,
    SubtractionType
} from "../../../../../api/generated";

export const REQUIRED_HEADERS = [
    "title",
    "description",
    "make",
    "model",
    "start date",
    "end date",
    "offer type",
    "redirect url",
    "image url",
]

export const DISCOUNT_HEADERS = [
    "discount type",
    "discount value",
]

export const FINANCE_HEADERS = [
    "currency",
    "finance product",
    "cash price",
    "monthly payments",
    "customer deposit",
    "manufacturer deposit contribution",
    "total deposit",
    "dealer contribution",
    "balance to finance",
    "total amount payable",
    "total charge for credit",
    "optional final payment",
    "duration of agreement in months",
    "apr",
    "fixed interest rate",
    "mileage per annum",
    "excess mileage charge",
]

export const REQUIRED_EXAMPLES = [
    "Example title. Lorem ipsum dolor sit amet",
    "Example description. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed euismod, ante quis tincidunt tincidunt, metus leo tincidunt erat, quis tincidunt metus ante quis turpis. Sed quis tincidunt metus.",
    "Specify make, or use '*' for all makes",
    "Specify model, or use '*' for all models",
    "2026-03-01",
    "2026-06-01",
    "Use one of those options: NEW_VEHICLE or USED_VEHICLE or SERVICE",
    "https://example.com/redirect-url",
    "https://example.com/image-url"
]

export const DISCOUNT_EXAMPLES = [
    "any number less or equals 100 if discount type is PERCENTAGE, else any number. MUST BE PROVIDED IF OFFER TYPE IS USED_VEHICLE or SERVICE",
    "Use one of those options: PERCENTAGE or NUMBER. MUST BE PROVIDED IF OFFER TYPE IS USED_VEHICLE or SERVICE",
]

export const FINANCE_EXAMPLES = [
    "For now it is only possible to use GBP. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "text value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "round number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
    "float number value. MUST BE PROVIDED IF OFFER TYPE IS NEW_VEHICLE",
]

export const parseCsv = (csvText: string): string[][] => {
    const rows: string[][] = [];
    const lines = csvText.replace(/\r/g, "").split("\n").filter(l => l.trim());

    for (const line of lines) {
        const result: string[] = [];
        let current = "";
        let insideQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];

            if (char === '"' && insideQuotes && nextChar === '"') {
                // Escaped quote ""
                current += '"';
                i++; // skip next
            } else if (char === '"') {
                insideQuotes = !insideQuotes;
            } else if (char === "," && !insideQuotes) {
                result.push(current.trim());
                current = "";
            } else {
                current += char;
            }
        }

        result.push(current.trim());
        rows.push(result);
    }

    return rows;
};

export const validateHeaders = (headers: string[]): string | null => {
    for (const required of REQUIRED_HEADERS) {
        if (!headers.includes(required)) {
            return `Missing required column: ${required}`;
        }
    }
    return null;
};

const isValidDate = (value: string) => {
    return !isNaN(Date.parse(value));
}

const isValidUrl = (value: string) => {
    try {
        new URL(value);
        return true;
    } catch {
        return false;
    }
};

const isPositiveNumber = (value: string) => {
    return value !== "" && !isNaN(Number(value)) && Number(value) >= 0;
}

const isDateExpired = (date: string) => {
    return new Date(date) < new Date();
}

const requireFields = (
    fields: Record<string, string>,
    rowNumber: number
): string | null => {
    for (const [name, value] of Object.entries(fields)) {
        if (!value) return `Row ${rowNumber}: Missing "${name}"`;
    }
    return null;
}

export const validateAndMapRows = (
    rows: string[][],
    headers: string[],
    fileName: string,
    publicBotId?: string,
    publicLocationId?: string
): { valid: boolean; error?: string; mapped?: CreateOfferRequest[] } => {

    const headerIndex = (name: string) => headers.indexOf(name);
    const mapped: CreateOfferRequest[] = [];

    const get = (row: string[], name: string) =>
        row[headerIndex(name)]?.trim() || "";

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const rowNumber = i + 2;

        const [
            title,
            description,
            make,
            model,
            startDate,
            endDate,
            offerTypeRaw,
            redirectUrl,
            imageUrl,
        ] = REQUIRED_HEADERS.map(h => get(row, h));

        const offerType = offerTypeRaw as OfferType;

        const baseError = requireFields(
            {title, description, make, model, startDate, endDate, offerType, redirectUrl, imageUrl},
            rowNumber
        );

        if (baseError) return {valid: false, error: baseError};

        /* ===== DATE VALIDATION ===== */
        if (!isValidDate(startDate) || !isValidDate(endDate))
            return {valid: false, error: `Row ${rowNumber}: Invalid date format`};

        if (isDateExpired(endDate))
            return {valid: false, error: `Row ${rowNumber}: End date is expired`};

        if (new Date(startDate) > new Date(endDate))
            return {valid: false, error: `Row ${rowNumber}: Start date must be before end date`};

        /* ===== URL VALIDATION ===== */
        if (!isValidUrl(redirectUrl))
            return {valid: false, error: `Row ${rowNumber}: Invalid redirect URL`};

        if (!isValidUrl(imageUrl))
            return {valid: false, error: `Row ${rowNumber}: Invalid image URL`};

        /* ===== OFFER TYPE ===== */
        if (!Object.values(OfferType).includes(offerType))
            return {valid: false, error: `Row ${rowNumber}: Invalid offer type`};

        /* ============================================================
           DISCOUNT VALIDATION (USED_VEHICLE, SERVICE)
        ============================================================ */
        let discount;

        if (offerType === OfferType.UsedVehicle || offerType === OfferType.Service) {
            const [discountTypeRaw, discountValueRaw] = DISCOUNT_HEADERS.map(h => get(row, h));
            const discountType = discountTypeRaw as SubtractionType;

            if (!discountType || !discountValueRaw)
                return {valid: false, error: `Row ${rowNumber}: Discount fields required for ${offerType}`};

            if (!Object.values(SubtractionType).includes(discountType))
                return {valid: false, error: `Row ${rowNumber}: Invalid discount type`};

            if (!isPositiveNumber(discountValueRaw))
                return {valid: false, error: `Row ${rowNumber}: Discount value must be a positive number`};

            const discountValue = Number(discountValueRaw);

            if (discountType === SubtractionType.Percentage && (discountValue < 0 || discountValue > 100))
                return {valid: false, error: `Row ${rowNumber}: Percentage discount must be between 0 and 100`};

            discount = {
                subtractionType: discountType,
                subtractionValue: discountValue
            };
        }

        /* ============================================================
           FINANCE VALIDATION (NEW_VEHICLE)
        ============================================================ */
        let financeData = {};

        if (offerType === OfferType.NewVehicle) {
            const values = FINANCE_HEADERS.map(h => get(row, h));

            if (values.some(v => v === ""))
                return {valid: false, error: `Row ${rowNumber}: All finance fields required for NEW_VEHICLE`};

            const [
                currency,
                financeProduct,
                cashPriceRaw,
                monthlyPaymentsRaw,
                customerDepositRaw,
                manufacturerDepositContributionRaw,
                totalDepositRaw,
                dealerContributionRaw,
                balanceToFinanceRaw,
                totalAmountPayableRaw,
                totalChargeForCreditRaw,
                optionalFinalPaymentRaw,
                durationRaw,
                aprRaw,
                fixedInterestRateRaw,
                mileagePerAnnumRaw,
                excessMileageChargeRaw
            ] = values;

            if (currency !== Currency.Gbp)
                return {valid: false, error: `Row ${rowNumber}: Invalid currency`};

            const numericFields = {
                cashPriceRaw,
                monthlyPaymentsRaw,
                customerDepositRaw,
                manufacturerDepositContributionRaw,
                totalDepositRaw,
                dealerContributionRaw,
                balanceToFinanceRaw,
                totalAmountPayableRaw,
                totalChargeForCreditRaw,
                optionalFinalPaymentRaw,
                durationRaw,
                aprRaw,
                fixedInterestRateRaw,
                mileagePerAnnumRaw,
                excessMileageChargeRaw
            };

            for (const [key, value] of Object.entries(numericFields)) {
                if (!isPositiveNumber(value))
                    return {valid: false, error: `Row ${rowNumber}: ${key} must be a positive number`};
            }

            if (Number(aprRaw) > 100 || Number(aprRaw) < 0) {
                return {valid: false, error: `Row ${rowNumber}: apr value must be between 0 and 100`};
            }

            if (Number(fixedInterestRateRaw) > 100 || Number(fixedInterestRateRaw) < 0) {
                return {valid: false, error: `Row ${rowNumber}: fixed interest rate value must be between 0 and 100`};
            }

            financeData = {
                currency,
                financeProduct,
                cashPrice: Number(cashPriceRaw),
                monthlyPayments: Number(monthlyPaymentsRaw),
                customerDeposit: Number(customerDepositRaw),
                manufacturerDepositContribution: Number(manufacturerDepositContributionRaw),
                totalDeposit: Number(totalDepositRaw),
                dealerContribution: Number(dealerContributionRaw),
                balanceToFinance: Number(balanceToFinanceRaw),
                totalAmountPayable: Number(totalAmountPayableRaw),
                totalChargeForCredit: Number(totalChargeForCreditRaw),
                optionalFinalPayment: Number(optionalFinalPaymentRaw),
                durationOfAgreementInMonths: Number(durationRaw),
                apr: Number(aprRaw),
                fixedInterestRate: Number(fixedInterestRateRaw),
                mileagePerAnnum: Number(mileagePerAnnumRaw),
                excessMileageCharge: Number(excessMileageChargeRaw),
            };
        }

        mapped.push({
            publicId: v4(),
            publicBotId,
            publicLocationId,
            title,
            description,
            make,
            model,
            startDate,
            endDate,
            offerType,
            redirectUrl,
            imageUrl,
            offerSource: OfferSource.Csv,
            offerSourceName: fileName,
            offerStatus: OfferStatus.Active,
            discount,
            ...financeData
        });
    }

    return {valid: true, mapped};
};

